import {Injectable} from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {apModalCancelled, apModalClosed, ApModalReason, ApModalResult} from 'webcommon/core-ui';
import {ImoSearchModalService} from 'webcommon/provider-api';
import {
  _ImoTermSearch,
  _Module, Gender,
  GuidStrValue, ImoGender,
  ImoPatient,
  ImoSearchResultDto,
  ImoTermDto,
  PatientDto,
  SessionRequest_Create
} from 'webcommon/shared';
import { ImoProblemService, TerminologyRepository } from 'webcommon/webapi';
import {ImoPatientFactory} from './ImoPatientFactory';
import {TerminologySearchModalService} from './terminology-search-modal.service';
import {isString} from 'lodash-es';
import { TerminologySearchComponent } from './terminology-search/terminology-search.component';

@Injectable({
  providedIn: 'root',
})
export class VersionedTerminologySearch {
  static readonly ajsFactoryName = 'VersionedTerminologySearchTs';

  constructor(
    private imoPatientFactory: ImoPatientFactory,
    private terminologySearchModalService: TerminologySearchModalService,
    private imoSearchModalService: ImoSearchModalService,
    private terminologyRepository: TerminologyRepository,
    private imoProblemService: ImoProblemService,
  ) { }

  // try to open Terminology search, if not, fallback to previous IMO search
  openModal$(sessionRequest: SessionRequest_Create): Observable<ApModalResult<ImoTermDto[]>> {
    if (!this.terminologySearchModalService.isSupported()) {
      const imoPatient = this.imoPatientFactory.create(
        null,
        sessionRequest.PatientAge,
        sessionRequest.PatientBirthSex,
      );
      return this.convertFromLegacy(this.imoSearchModalService.openModal(
        imoPatient,
        sessionRequest.InitialSearchText || '',
        !sessionRequest.IsSingleSelect,
      ));
    }

    return this.terminologySearchModalService.openModal$(sessionRequest);
  }

  // try to open Terminology search, if not, fallback to previous IMO search
  openModal2$(
    patient: PatientDto | null,
    searchText: string,
    multiSelect: boolean,
    hccOnly = false,
  ): Observable<ApModalResult<ImoTermDto[]>> {
    if (!this.terminologySearchModalService.isSupported()) {
      return this.convertFromLegacy(this.imoSearchModalService.openModal(
        patient,
        searchText,
        multiSelect,
        hccOnly,
      ));
    }

    return this.terminologySearchModalService.openModal2$(patient, searchText, multiSelect, hccOnly);
  }

  simpleSearch$(
    patient: PatientDto | null,
    searchText: string,
    resultsPerPage?: number
  ): Observable<ImoSearchResultDto> {
    const config: _ImoTermSearch = {
      SearchQuery: searchText,
      DymSize: 0,
      ResultsPerPage: resultsPerPage,
    };

    const imoPatient = this.imoPatientFactory.mapFromApi(patient);
    if (imoPatient && imoPatient.hasAge) {
      config.PatientAge = imoPatient.standardizedAge;
    }
    if (imoPatient && imoPatient.hasGender && imoPatient.standardizedGender !== Gender.U) {
      config.PatientGender = ImoGender[imoPatient.standardizedGender];
    }

    return this.imoProblemService.search(config);
  }

  // convert the results of old modal to match structure of new modal results
  convertFromLegacy(legacyModalResult: Promise<ImoTermDto | ImoTermDto[] | null>): Observable<ApModalResult<ImoTermDto[]>> {
    return from(legacyModalResult).pipe(
      map((result) => {
        if (!result) {
          // Currently, this specific modal can return null if you don't select anything.
          // We are now treating that as a cancel to have consistent behavior with TerminologySearchModalComponent.
          return apModalCancelled<ImoTermDto[]>(ApModalReason.UserCancel);
        }
        const results = Array.isArray(result) ? result : [result];
        if (!results.length) {
          // We are now treating an empty list as a cancel to have consistent behavior with TerminologySearchModalComponent.
          return apModalCancelled<ImoTermDto[]>(ApModalReason.UserCancel);
        }
        return apModalClosed<ImoTermDto[]>(results);
      }),
      catchError((error: unknown) => {
        if (!error) {
          // for this specific modal, if undefined error,
          // then it's not an actual error, it really means the modal was cancelled
          return of(apModalCancelled<ImoTermDto[]>(ApModalReason.UserCancel));
        }
        if (isString(error)) {
          // the error callback can also happen as a result of $uibModalStack.dismissAll
          return of(apModalCancelled<ImoTermDto[]>(ApModalReason.Unknown, error));
        }
        return throwError(error);
      }),
    );
  }

  // ==========================================================================================
  // All of this below is currently used in angularjs as well, so be careful when changing the interface of it.
  /* eslint-disable @typescript-eslint/member-ordering */
  _isNewSearchSupported = TerminologySearchComponent.isSupportedFn();

  isNewSearchSupported(ctrl: DiagnosisSearchMainCtrl): boolean {
    if (!this._isNewSearchSupported()) {
      return false;
    }

    // todo: handle these cases that are not currently supported/implemented by Terminology Search
    if (ctrl.dxRange ||
      ctrl.searchDefinition ||
      ctrl.searchDefinitionSpecifiedCodeUse ||
      ctrl.lexicalCodes
    ) {
      this.warnInvalidParams(ctrl);
      return false;
    }
    return true;
  }

  // return a SessionRequest that can be used with new Terminology Search,
  // or null, if it's not supported
  tryCreateSessionRequest(ctrl: DiagnosisSearchMainCtrl): SessionRequest_Create | null {
    if (!this.isNewSearchSupported(ctrl)) {
      return null;
    }

    return this.createSessionRequest(ctrl);
  }

  // return a SessionRequest that can be used with new Terminology Search
  createSessionRequest(ctrl: DiagnosisSearchMainCtrl): SessionRequest_Create {
    const session: SessionRequest_Create = {
      AllowNonBillableCodes: !ctrl.onlyBillableCodes,
      HccOnly: ctrl.hccModalFiltered,
      IncludeAdditionalCodings: true,
      IsSingleSelect: !ctrl.multiSelect,
      ModuleId: _Module.ImoProblemSearch,
      InitialSearchText: ctrl.query || null,
      HideSingleSelectSaveAndCancelButtons: true,
      PRM_UseHccDefaultDisplayUserSetting: true,
    };
    if (ctrl.patient && ctrl.patient.hasAge) {
      session.PatientAge = ctrl.patient.standardizedAge;
    }
    if (ctrl.patient && ctrl.patient.hasGender) {
      session.PatientBirthSex = ctrl.patient.standardizedGender;
    }
    return session;
  }

  warnInvalidParams(ctrl: DiagnosisSearchMainCtrl): void {
    console.warn('The state coming from the DiagnosisSearch page cannot be converted' +
      ' to a valid request to Terminology because of a value in dxRange OR searchDefinition' +
      ' OR searchDefinitionSpecifiedCodeUse OR lexicalCodes.' +
      ' We have to fallback to old search', ctrl);
  }

  getSessionTerms(sessionToken: GuidStrValue): Observable<ImoTermDto[]> {
    return this.terminologyRepository.getSessionTerms(sessionToken);
  }
  /* eslint-enable @typescript-eslint/member-ordering */
  // End of stuff currently used in angularjs
  // ==========================================================================================
}

// this type corresponds to the data that exists after initialization in
// ajs DiagnosisSearchMainCtrl
interface DiagnosisSearchMainCtrl {
  multiSelect: boolean;
  // selectedTerms is just used to populate the state of the control, like from a FindControl (MobileFindDiagnosis)
  // and is expected to match the same type as what the control returns
  selectedTerms: ImoTermDto[] | ImoTermDto | undefined;
  // query is the initial search text
  query: string | undefined;
  onlyBillableCodes: boolean;
  // hideResults has only been set to true for a few cases historically:
  // 1) old charge capture in AppMobile doesn't navigate to this page but it does pass in true to diagnosis-search
  // via versioned-diagnosis-search
  //
  // 2) ControlDiagnosisSearchDefinition: Range of Codes;
  // this was removed at some point, so it's not used there anymore
  // 3) range of codes for ControlAddDiagnosisCodeOrRange (User Defined Control Group in list editor);
  // this was commented out at some point, so it's not used there anymore
  //
  // It was mainly useful for cases where the search was inline, instead of in a modal,
  // and the results were getting processed one at a time after selection, so there was no reason to show the results
  // It seems like it can be ignored for now for the Terminology Search
  hideResults: boolean;
  // 1 example for dxRange is in the charge ticket on DX tab:
  // clicking an item associated to a Diagnosis Search Definition with Code Range filled in
  // So, it could be something like this: C00.0;C00.1;C00, and on the server side,
  // it's split by , or ; into each separate code or range in FilterExpressionHelper.FilterToRange()
  // Each range is a format of xx-xx, which results in ICD10CM_CODE = or >= or <= in the FilterByExpression property
  // of the actual IMO request.
  // The example above would turn into (ICD10CM_CODE = C00.0)|(ICD10CM_CODE = C00.1)|(ICD10CM_CODE = C00)
  dxRange: string | undefined;
  // searchDefinition is just the name of the ListDiagnosisSearchDefinition,
  // which was shown at the top of the search, along with the dxRange associated to it
  searchDefinition: string | undefined;
  // The searchDefinition could also have specific LexicalCodes,
  // which it can completely limit the search to,
  // or just prepopulate the search results with those specific codes, but then allow anything to be searched
  // values from CodeSearchDefinition.cs:
  // 0 = None
  // 1 = Prepopulate
  // 2 = Limited To
  searchDefinitionSpecifiedCodeUse: '0' | '1' | '2' | undefined;
  // lexicalCodes are also associated with the ListDiagnosisSearchDefinition,
  // as described by the notes about searchDefinitionSpecifiedCodeUse.
  // But, this could be used for other cases if we wanted to.
  lexicalCodes: string[] | undefined;
  hccModalFiltered: boolean;
  patient?: ImoPatient | null;
}
